/**
 * Transports — metadata shared by admin editor and public Welcome Book.
 *
 * Single source of truth for:
 *   • category order + id
 *   • label translations (5 languages already supported)
 *   • accent colour (same across admin + frontend, so hosts recognise their
 *     categories visually)
 *   • icon (Lucide, outline, uniform stroke ─ matches the existing icon set)
 *   • shape: "list" | "tabs" | "sub" | "custom" (see docstring below)
 *
 * Shape definitions
 * ─────────────────
 * list   – plain list of items       → { active, items: [] }
 * tabs   – up to N labelled tabs     → { active, tabs: [{ id, title, items }] }
 * sub    – fixed sub-categories      → { active, sub: { key: { active, items } } }
 * custom – multi "Other" cards       → array of { id, title, active, items }
 *
 * Item shape (all fields optional except `name`):
 *   { id, name, description, website, phone, apple_app, google_play }
 */
import {
  Bus, Car, Bike, KeyRound, CarFront, Plane, TrainFront, MapPin, Compass,
} from "lucide-react";

// Category → accent colour. Chosen from the same palette used across the
// Welcome Book (Tailwind saturated 500/600). Do NOT diverge here or the
// admin/frontend colours will drift out of sync.
export const TRANSPORT_CATEGORIES = [
  {
    id: "public_transport",
    color: "#10B981",                                // emerald-500
    gradient: "linear-gradient(135deg,#34D399 0%,#059669 60%,#065F46 100%)",
    icon: Bus,
    shape: "list",
    labels: { it: "Trasporto pubblico", en: "Public transport", fr: "Transports publics", es: "Transporte público", de: "Öffentlicher Verkehr" },
  },
  {
    id: "car_sharing",
    color: "#6366F1",                                // indigo-500
    gradient: "linear-gradient(135deg,#818CF8 0%,#4F46E5 60%,#3730A3 100%)",
    icon: Car,
    shape: "tabs",
    maxTabs: 2,
    labels: { it: "Sharing", en: "Sharing", fr: "Sharing", es: "Sharing", de: "Sharing" },
  },
  {
    id: "bike_sharing",
    color: "#84CC16",                                // lime-500
    gradient: "linear-gradient(135deg,#A3E635 0%,#65A30D 60%,#3F6212 100%)",
    icon: Bike,
    shape: "tabs",
    maxTabs: 2,
    labels: { it: "Bike sharing", en: "Bike sharing", fr: "Vélopartage", es: "Bike sharing", de: "Bikesharing" },
  },
  {
    id: "car_rental",
    color: "#F59E0B",                                // amber-500
    gradient: "linear-gradient(135deg,#FBBF24 0%,#D97706 60%,#92400E 100%)",
    icon: KeyRound,
    shape: "list",
    labels: { it: "Noleggio auto", en: "Car rental", fr: "Location de voiture", es: "Alquiler de coches", de: "Autovermietung" },
  },
  {
    id: "taxi",
    color: "#EAB308",                                // yellow-500
    gradient: "linear-gradient(135deg,#FACC15 0%,#CA8A04 60%,#854D0E 100%)",
    icon: Car,
    shape: "list",
    labels: { it: "Taxi", en: "Taxi", fr: "Taxi", es: "Taxi", de: "Taxi" },
  },
  {
    id: "ncc",
    color: "#475569",                                // slate-600
    gradient: "linear-gradient(135deg,#64748B 0%,#334155 60%,#0F172A 100%)",
    icon: CarFront,
    shape: "list",
    labels: { it: "NCC — Noleggio con conducente", en: "NCC — Private driver", fr: "VTC", es: "VTC", de: "Chauffeurdienst" },
  },
  {
    id: "airport",
    color: "#0EA5E9",                                // sky-500
    gradient: "linear-gradient(135deg,#38BDF8 0%,#0284C7 60%,#075985 100%)",
    icon: Plane,
    shape: "sub",
    subs: [
      { id: "bus",   icon: Bus,        labels: { it: "Bus / navetta", en: "Bus / shuttle", fr: "Bus / navette", es: "Bus / lanzadera", de: "Bus / Shuttle" } },
      { id: "train", icon: TrainFront, labels: { it: "Treno",         en: "Train",         fr: "Train",        es: "Tren",             de: "Zug" } },
      { id: "taxi",  icon: Car,        labels: { it: "Taxi",          en: "Taxi",          fr: "Taxi",         es: "Taxi",             de: "Taxi" } },
      { id: "ncc",   icon: CarFront,   labels: { it: "NCC",           en: "Private driver", fr: "VTC",         es: "VTC",              de: "Chauffeurdienst" } },
    ],
    labels: { it: "Trasporti aeroporto", en: "Airport transport", fr: "Transports aéroport", es: "Transportes aeropuerto", de: "Flughafentransport" },
  },
  {
    id: "other",
    color: "#71717A",                                // zinc-500
    gradient: "linear-gradient(135deg,#A1A1AA 0%,#52525B 60%,#27272A 100%)",
    icon: MapPin,
    shape: "custom",
    labels: { it: "Altro", en: "Other", fr: "Autre", es: "Otro", de: "Sonstiges" },
  },
];

// Section-level accent (used by the Home tile and the Explore top button).
export const TRANSPORTS_ACCENT = {
  color: "#0EA5E9",
  gradient: "linear-gradient(135deg,#38BDF8 0%,#0284C7 60%,#075985 100%)",
  icon: TrainFront,
};

// Section title
export const TRANSPORTS_TITLE = {
  it: "Trasporti locali", en: "Local transport", fr: "Transports locaux", es: "Transporte local", de: "Öffentlicher Verkehr",
};
export const TRANSPORTS_SUBTITLE = {
  it: "Come muoversi in zona", en: "How to get around", fr: "Comment se déplacer", es: "Cómo moverse", de: "So bewegst du dich",
};

// Field labels (admin + frontend action buttons)
export const TRANSPORT_FIELDS = {
  name:         { it: "Nome del servizio",  en: "Service name",   fr: "Nom du service",   es: "Nombre del servicio", de: "Servicename" },
  description:  { it: "Descrizione breve",  en: "Short description", fr: "Description brève", es: "Descripción breve", de: "Kurzbeschreibung" },
  website:      { it: "Sito web",           en: "Website",        fr: "Site web",         es: "Sitio web",         de: "Webseite" },
  phone:        { it: "Numero di telefono", en: "Phone number",   fr: "Numéro de téléphone", es: "Número de teléfono", de: "Telefonnummer" },
  apple_app:    { it: "Link App Store (iOS)", en: "App Store link", fr: "Lien App Store", es: "Enlace App Store",  de: "App-Store-Link" },
  google_play:  { it: "Link Google Play (Android)", en: "Google Play link", fr: "Lien Google Play", es: "Enlace Google Play", de: "Google-Play-Link" },
};

export const TRANSPORT_ACTIONS = {
  website:     { it: "Sito web",     en: "Website",   fr: "Site web",   es: "Sitio web",   de: "Webseite" },
  call:        { it: "Chiama",       en: "Call",      fr: "Appeler",    es: "Llamar",      de: "Anrufen" },
  app_store:   { it: "App Store",    en: "App Store", fr: "App Store",  es: "App Store",   de: "App Store" },
  google_play: { it: "Google Play",  en: "Google Play", fr: "Google Play", es: "Google Play", de: "Google Play" },
};

// Get the meta object for a category id.
export const getCategory = (id) => TRANSPORT_CATEGORIES.find((c) => c.id === id);
